import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, Flame, Droplet, Building2, Wrench, Phone, Quote, CloudDrizzle } from "lucide-react";
import { BRAND, SERVICE_CATEGORIES, SUB_BRANDS, VALUES, DIFFERENTIATORS, PROJECTS } from "../data/content";
import { PAGE_SEO } from "../data/seoContent";
import { FAQS, faqSchema } from "../data/faqs";
import SEO from "../components/SEO";
import FaqAccordion from "../components/FaqAccordion";
import AccreditationsStrip from "../components/sections/Accreditations";

const SERVICE_ICONS = {
  asbestos: ShieldCheck,
  "fire-safety": Flame,
  "water-hygiene": Droplet,
  compliance: Building2,
  "damp-mould": CloudDrizzle,
  remedial: Wrench,
};

export default function Home() {
  return (
    <div data-testid="page-home">
      <SEO {...PAGE_SEO.home} schema={faqSchema(FAQS.home)} />
      {/* HERO */}
      <section className="relative bg-[#0A0F1C] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://images.pexels.com/photos/14526255/pexels-photo-14526255.jpeg?auto=compress&cs=tinysrgb&w=1280&dpr=1"
            alt=""
            width="1280"
            height="853"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0F1C]/60 via-[#0A0F1C]/80 to-[#0A0F1C]"></div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-28 lg:pt-32 lg:pb-40">
          <div className="max-w-3xl gs-fade-up">
            <p className="text-[11px] uppercase tracking-[0.3em] text-white/60 mb-8" data-testid="hero-eyebrow">
              Oxford Environmental Consultants · Property Compliance Experts
            </p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05]">
              Property Compliance.<br/>
              <span className="gs-gradient-text whitespace-nowrap">Delivered&nbsp;Professionally.</span>
            </h1>
            <p className="mt-8 text-lg text-white/70 max-w-xl leading-relaxed">
              One-stop, multi-discipline risk management across asbestos, fire safety, water hygiene, damp &amp; mould and building safety — delivered with audit-ready precision, nationwide.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" className="gs-gradient text-white font-semibold px-8 py-4 flex items-center gap-2 hover:opacity-90 shadow-lg shadow-[#3FBAB0]/30" data-testid="hero-cta-quote">
                Get Your Free Compliance Quote <ArrowUpRight className="w-4 h-4" />
              </Link>
              <a href={`tel:${BRAND.phone.replace(/\s/g,"")}`} className="border border-white/30 text-white font-semibold px-8 py-4 hover:bg-white hover:text-[#0A0F1C] transition-colors flex items-center gap-2" data-testid="hero-cta-call">
                <Phone className="w-4 h-4" /> {BRAND.phone}
              </a>
            </div>
            <p className="mt-5 text-xs text-white/50">Response within 1 working day · No-obligation site visit available · UK-wide coverage</p>
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-8 max-w-2xl">
              {[
                { n: "5,000+", l: "Inspections / year" },
                { n: "6", l: "Specialist divisions" },
                { n: "UK-wide", l: "Essex · London · Kent" },
                { n: "ISO Accredited", l: "Audit-ready" },
              ].map((s, i) => (
                <div key={i} className="border-l border-white/20 pl-4">
                  <p className="font-display text-2xl font-bold text-white">{s.n}</p>
                  <p className="text-xs text-white/50 mt-1 uppercase tracking-wider">{s.l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <AccreditationsStrip />

      {/* MISSION STATEMENT */}
      <section className="bg-white py-20 lg:py-24 border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">Our mission</p>
            <h2 className="font-display text-4xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              Compliance Without<br /><span className="gs-gradient-text">Compromise.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-center">
            <div>
              <Quote className="w-8 h-8 text-[#3FBAB0] mb-6" strokeWidth={1.2} />
              <p className="text-xl text-slate-700 leading-relaxed">
                Oxford Environmental Consultants Limited is a multi-disciplinary property compliance and safety specialist supporting commercial, industrial and residential clients across Essex, London, Kent and the wider UK.
              </p>
              <p className="text-base text-slate-600 leading-relaxed mt-5">
                We help duty holders keep people safe, stay compliant and protect their buildings — through clear, practical advice and reports that stand up to tender scrutiny, HSE inspection and ISO audit.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="bg-white py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
            <div className="lg:col-span-4">
              <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">01 · Services</p>
              <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">Six pillars. One group.</h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex items-end">
              <p className="text-slate-600 text-lg leading-relaxed">
                A single accountable team across every discipline a duty holder must manage. Each service is delivered under aligned UK legislation and ISO-aligned management systems — removing the overhead of multi-supplier compliance.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {SERVICE_CATEGORIES.map((s) => {
              const Icon = SERVICE_ICONS[s.slug] || ShieldCheck;
              return (
                <Link key={s.slug} to={`/services/${s.slug}`} className="group relative bg-white p-8 lg:p-10 hover:bg-[#0A0F1C] hover:text-white transition-colors" data-testid={`home-service-${s.slug}`}>
                  <div className="flex items-start justify-between mb-8">
                    <Icon className="w-8 h-8 text-[#1FA59A] group-hover:text-white transition-colors" strokeWidth={1.5} />
                    <ArrowUpRight className="w-5 h-5 text-slate-300 group-hover:text-white transition-colors" />
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 group-hover:text-white/50 mb-2">{s.brand}</p>
                  <h3 className="font-display text-2xl lg:text-3xl font-semibold mb-3">{s.name}</h3>
                  <p className="text-slate-600 group-hover:text-white/70 leading-relaxed">{s.summary}</p>
                  <p className="text-[10px] uppercase tracking-[0.2em] mt-6 text-slate-400 group-hover:text-white/40">{s.legislation}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* GROUP STRUCTURE */}
      <section className="bg-[#F8FAFC] py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
            <div className="lg:col-span-4">
              <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">02 · Group Structure</p>
              <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">Specialists within a single group.</h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex items-end">
              <p className="text-slate-600 text-lg leading-relaxed">
                Oxford Environmental Consultants operates two specialist trading brands alongside its in-house consultancy practice. Each division retains the focus of a discipline-led consultancy, underpinned by the governance, ISO systems and national resource of the wider OEC group.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {Object.values(SUB_BRANDS).map((b) => (
              <a key={b.slug} href={b.website} target="_blank" rel="noreferrer" className="group block bg-white border border-slate-200 hover:border-slate-900 transition-colors" data-testid={`home-brand-${b.slug}`}>
                <div className="h-96 lg:h-[28rem] flex items-center justify-center bg-white border-b border-slate-200 p-10">
                  <img src={b.logo} alt={b.name} loading="lazy" decoding="async" width="200" height="200" className="max-h-full max-w-full object-contain" />
                </div>
                <div className="p-10 lg:p-12">
                  <div className="flex items-center gap-2 mb-5">
                    <span className="text-[9px] uppercase tracking-[0.25em] font-semibold text-white bg-[#0A0F1C] px-2 py-1">Part of Oxford Environmental Consultants</span>
                  </div>
                  <h3 className="font-display text-3xl lg:text-4xl font-semibold text-slate-900 mb-3">{b.name}</h3>
                  <p className="text-slate-600 text-lg mb-5">{b.tagline}</p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{b.legislation}</p>
                  <div className="mt-8 flex items-center text-sm font-semibold text-slate-900 gap-2 group-hover:gap-4 transition-all">
                    Visit {b.name} <ArrowUpRight className="w-4 h-4" />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-white py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
            <div className="lg:col-span-5">
              <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">03 · Why OEC</p>
              <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
                The consultancy that<br />property managers trust.
              </h2>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
                {VALUES.map((v) => (
                  <div key={v.title} className="bg-white p-8">
                    <p className="font-display text-xl font-semibold text-slate-900 mb-3">{v.title}</p>
                    <p className="text-slate-600 leading-relaxed">{v.copy}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DIFFERENTIATORS */}
      <section className="bg-[#0A0F1C] text-white py-24 lg:py-32 relative">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
            <div className="lg:col-span-5">
              <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4">04 · Differentiators</p>
              <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">What makes <span className="gs-gradient-text">OEC</span> different.</h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex items-end">
              <p className="text-white/70 text-lg leading-relaxed">
                Most consultancies hold expertise in one discipline. OEC holds four — under one roof, one governance framework and one point of accountability.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {DIFFERENTIATORS.map((d) => (
              <div key={d.n} className="bg-[#0A0F1C] p-8 min-h-[280px] flex flex-col">
                <p className="font-display text-sm text-[#3FBAB0] mb-6">{d.n}</p>
                <p className="font-display text-2xl font-semibold mb-4">{d.title}</p>
                <p className="text-white/60 leading-relaxed text-sm">{d.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECTS TEASE */}
      <section className="bg-[#F8FAFC] py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">05 · Projects</p>
              <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">Delivered for landmark clients.</h2>
            </div>
            <Link to="/projects" className="text-sm font-semibold text-slate-900 gs-link-underline" data-testid="home-view-all-projects">View all projects →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PROJECTS.map((p, i) => (
              <Link key={i} to="/projects" className="bg-white border border-slate-200 group overflow-hidden hover:border-slate-900 transition-colors" data-testid={`home-project-${i}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                </div>
                <div className="p-6">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">{p.sector}</p>
                  <p className="font-display text-xl font-semibold text-slate-900 mb-2">{p.title}</p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#1FA59A] font-semibold mb-3">{p.discipline}</p>
                  <p className="text-sm text-slate-600 leading-relaxed">{p.summary}</p>
                  <p className="mt-4 text-sm font-semibold text-slate-900 flex items-center gap-2 group-hover:gap-3 transition-all">Read case study <ArrowUpRight className="w-4 h-4" /></p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A0F1C] text-white py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] gs-gradient" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#7DD3C7] mb-5 font-semibold">Free site visit · No obligation</p>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
              Speak to an OEC expert.
            </h2>
            <p className="mt-6 text-white/70 text-lg max-w-lg leading-relaxed">
              Tell us about your estate. Our team will scope a compliance programme tailored to your portfolio, sector and legislative exposure — typically within 24&nbsp;hours.
            </p>
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-white/70 max-w-lg">
              <li className="flex items-start gap-2"><span className="text-[#3FBAB0] font-bold">✓</span> No-cost initial scoping call</li>
              <li className="flex items-start gap-2"><span className="text-[#3FBAB0] font-bold">✓</span> Fixed-price quotes within 48 hours</li>
              <li className="flex items-start gap-2"><span className="text-[#3FBAB0] font-bold">✓</span> Audit-ready reporting included</li>
              <li className="flex items-start gap-2"><span className="text-[#3FBAB0] font-bold">✓</span> National coverage</li>
            </ul>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 lg:justify-end">
            <a href={`tel:${BRAND.phone.replace(/\s/g,"")}`} className="border border-white/30 px-8 py-4 flex items-center gap-3 hover:bg-white hover:text-[#0A0F1C] transition-colors justify-center" data-testid="home-cta-call">
              <Phone className="w-4 h-4" /> {BRAND.phone}
            </a>
            <Link to="/contact" className="gs-gradient px-8 py-4 font-bold flex items-center gap-2 hover:opacity-90 justify-center shadow-lg shadow-[#3FBAB0]/30" data-testid="home-cta-contact">
              Get Your Free Quote <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <FaqAccordion
        faqs={FAQS.home}
        title="Property compliance — questions we hear a lot."
        subtitle="From asbestos surveys and fire risk assessments to mould investigations and legionella risk assessments — the questions duty-holders most often ask before appointing a consultancy."
      />
    </div>
  );
}
